import React, { useEffect, useMemo, useRef, useState } from "react";

import { useFormContext } from "react-hook-form";

import { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  getFieldKey,
  getFieldLabel,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { hasEnumData } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";
import { matchFieldDisplay } from "@fartherfinance/frontend/api/TaskWorkflows/utils/patternMatching";

import { ExternalResources, ValidationRules } from "../../core/types";
import { resolveExpressions } from "../../expressions/resolveExpressions";
import { fieldEnhancementRegistry } from "../enhancements/FieldEnhancementRegistry";
import { useFieldDependencies } from "../hooks/useFieldDependencies";
import { getFieldComponent } from "../registry";
import { buildRules } from "../validation/buildRules";

interface FieldRendererProps {
  fieldParameter: FieldParameter;
  externalResources?: ExternalResources;
  options?: Array<{ label: string; value: string }>;
  isLoading?: boolean;
  onClear?: () => void;
}

const FieldRenderer = ({
  fieldParameter,
  externalResources,
  options = [],
  isLoading = false,
  onClear,
}: FieldRendererProps): JSX.Element => {
  const { control, getValues, setValue, watch, clearErrors } = useFormContext();

  const { dependencies, dependencyValues } =
    useFieldDependencies(fieldParameter);

  // State for async validation rules
  const [rules, setRules] = useState<ValidationRules>({ required: false });
  const [_rulesLoading, setRulesLoading] = useState(false);

  // Handle async validation rules
  useEffect(() => {
    const resolveValidationRules = async () => {
      setRulesLoading(true);
      try {
        const resolvedRules = await buildRules(
          fieldParameter,
          getValues,
          externalResources
        );
        setRules(resolvedRules);
      } catch (error) {
        console.warn("Error building validation rules:", error);
        // Fallback to safe defaults
        setRules({ required: false });
      } finally {
        setRulesLoading(false);
      }
    };

    resolveValidationRules();
  }, [
    fieldParameter,
    getValues,
    externalResources,
    dependencies.allDependencies,
    dependencyValues,
  ]);

  // Clear validation errors when required state changes
  const prevRequiredRef = useRef(rules.required);
  useEffect(() => {
    const currentRequired = rules.required;
    const prevRequired = prevRequiredRef.current;

    // Only clear errors if required state actually changed
    if (prevRequired !== currentRequired) {
      clearErrors(fieldParameter.key);
      prevRequiredRef.current = currentRequired;
    }
  }, [rules.required, fieldParameter.key, clearErrors]);

  // Check if field has enhancement first (performance optimization)
  const hasEnhancement =
    fieldEnhancementRegistry.hasEnhancement(fieldParameter);

  // Only watch the current value if there's an enhancement available
  const currentValue = hasEnhancement ? watch(fieldParameter.key) : undefined;
  const currentFieldValue: string | undefined =
    typeof currentValue === "string" ? currentValue : currentValue?.value;

  // Get enhancement config (if any) - single lookup instead of hasEnhancement + getEnhancement
  const enhancement = hasEnhancement
    ? fieldEnhancementRegistry.getEnhancement(fieldParameter, currentFieldValue)
    : undefined;

  const Comp = useMemo(() => {
    return getFieldComponent(fieldParameter);
  }, [fieldParameter]);

  // Create enhanced label with optional indicator
  const enhancedLabel = useMemo(() => {
    const baseLabel = getFieldLabel(fieldParameter);
    const isRequired = rules.required !== false;

    // Only add "(Optional)" if field is not required and label doesn't already contain it
    if (
      !isRequired &&
      baseLabel &&
      !baseLabel.toString().includes("(Optional)")
    ) {
      return `${baseLabel} (Optional)`;
    }

    return baseLabel;
  }, [fieldParameter, rules.required]);

  // State for async enabled resolution
  const [isEnabled, setIsEnabled] = useState(true); // Default to enabled
  const [isEnabledLoading, setIsEnabledLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Handle async enabled expressions
  useEffect(() => {
    const resolveEnabledState = async () => {
      if (!fieldParameter.display || !("enabled" in fieldParameter.display)) {
        setIsEnabled(true);
        return;
      }

      const { enabled } = fieldParameter.display;

      if (typeof enabled === "boolean") {
        setIsEnabled(enabled);
        return;
      }

      if (Array.isArray(enabled)) {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setIsEnabledLoading(true);
        // Start with disabled state while loading (assume falsy)
        setIsEnabled(false);

        try {
          const enabledResults = await resolveExpressions(
            enabled,
            getValues,
            externalResources
          );

          // Check if request was aborted
          if (abortControllerRef.current.signal.aborted) {
            return;
          }

          // The field is enabled if all results are truthy
          const finalEnabled = enabledResults
            ? enabledResults.every((result) => Boolean(result))
            : true;

          setIsEnabled(finalEnabled);
        } catch (error) {
          // On error, default to disabled (assume falsy)
          if (!abortControllerRef.current.signal.aborted) {
            setIsEnabled(false);
            console.warn("Error resolving enabled expressions:", error);
          }
        } finally {
          if (!abortControllerRef.current.signal.aborted) {
            setIsEnabledLoading(false);
          }
        }
      } else {
        setIsEnabled(true);
      }
    };

    resolveEnabledState();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    fieldParameter,
    getValues,
    externalResources,
    dependencies.allDependencies,
    dependencyValues,
  ]);

  const fieldProps = useMemo(() => {
    const isEnumDataField = hasEnumData(fieldParameter);
    const isFieldLoading = isEnumDataField && isLoading;

    // Basic props for the field using API utilities
    const baseProps = {
      name: getFieldKey(fieldParameter),
      label: enhancedLabel, // Use enhanced label instead of getFieldLabel
      rules: rules,
      control: control,
      disabled: isFieldLoading || !isEnabled || isEnabledLoading,
    };

    const commonProps = {
      ...baseProps,
      placeholder:
        "placeholderText" in fieldParameter.display
          ? fieldParameter.display.placeholderText
          : undefined,
      helperText:
        "helperText" in fieldParameter.display
          ? fieldParameter.display.helperText
          : undefined,
      labelTooltip:
        "infoBubbleText" in fieldParameter.display
          ? fieldParameter.display.infoBubbleText
          : undefined,
    };

    // Use API pattern matching for field props
    return matchFieldDisplay(fieldParameter, {
      SelectField: (display, field) => {
        const selectProps = {
          ...commonProps,
          values: options,
          getOptionKey: (option: { value: string | number }) => option.value,
          onClear:
            options.length > 0
              ? () => {
                  setValue(getFieldKey(field), null, { shouldDirty: true });
                }
              : onClear,
          isLoading: isFieldLoading,
          searchable: display.searchable,
          multi: display.multi,
        };

        // Apply field enhancements if available
        if (enhancement) {
          // Enrich options with metadata if enhancer provides it
          const enrichedOptions = enhancement.enrichOptions
            ? enhancement.enrichOptions(options)
            : options;

          return {
            ...selectProps,
            values: enrichedOptions,
            ...(enhancement.renderOption && {
              renderOption: enhancement.renderOption,
            }),
            ...(enhancement.renderStartAdornment && {
              renderStartAdornment: enhancement.renderStartAdornment,
            }),
            ...(enhancement.getGroupBy && {
              groupBy: enhancement.getGroupBy(),
            }),
          };
        }

        return selectProps;
      },

      InputField: (display) => ({
        ...commonProps,
        ...("multiline" in display && display.multiline ? { minRows: 5 } : {}),
      }),

      SelectAccountField: (display) => ({
        ...commonProps,
        values: options,
        getOptionKey: (option: { value: string | number }) => option.value,
        onClear:
          options.length > 0
            ? () => {
                setValue(getFieldKey(fieldParameter), null, {
                  shouldDirty: true,
                });
              }
            : onClear,
        isLoading: isFieldLoading,
        searchable: display.searchable,
        multi: display.multi,
      }),

      DueDateInputField: () => commonProps,

      RadioButtonField: () => ({
        ...commonProps,
        values: options,
      }),

      Default: () => commonProps,
    });
  }, [
    fieldParameter,
    enhancedLabel,
    rules,
    control,
    options,
    isLoading,
    setValue,
    onClear,
    enhancement,
    isEnabled,
    isEnabledLoading,
  ]);

  return <Comp {...(fieldProps as object)} />;
};

export default React.memo(FieldRenderer);
